<template>
  <div class="canvas">
    <div class="lane-head">
      <h2><VnIcon name="section" />بخش‌ها و پرسش‌ها</h2>
      <span class="lane-meta">{{ fa(activeSections.length) }} بخش · {{ fa(activeQuestions) }} پرسش</span>
    </div>

    <draggable :list="branch.sections" :group="{ name: 'sections', pull: false, put: ['sections'] }" handle=".s-grip" :animation="160"
               ghost-class="dz-ghost" class="sections" :class="{ empty: !branch.sections.length }" @add="onSectionAdded">
      <section v-for="(section, sIndex) in branch.sections" v-show="showArchived || section.is_active" :key="section._k" class="sblock"
               :class="{ selected: isSelected(section), archived: !section.is_active, collapsed: collapsed[section._k] }">
        <header class="shead" role="button" tabindex="0" @click="select({ kind: 'section', item: section })" @keydown.enter.self.prevent="select({ kind: 'section', item: section })">
          <span class="grip s-grip" title="جابه‌جایی بخش" aria-hidden="true"><VnIcon name="grip" /></span>
          <span class="sindex vn-num">{{ fa(sIndex + 1) }}</span>
          <div class="stitle">
            <strong>{{ section.verbose_name || 'بخش بی‌نام' }}</strong>
            <small>
              {{ fa(section.questions.filter(q => q.is_active).length) }} پرسش
              <template v-if="section.is_mandatory"> · بخش اجباری</template>
              <template v-if="reportName(section.report_category)"> · {{ reportName(section.report_category) }}</template>
              <template v-if="!section.is_active"> · بایگانی‌شده</template>
            </small>
          </div>
          <div class="stools" @click.stop>
            <button type="button" class="add-q" title="افزودن پرسش" @click="$emit('add-question-to', section)"><VnIcon name="plus" />پرسش</button>
            <button type="button" :title="collapsed[section._k] ? 'باز کردن' : 'جمع کردن'" @click="$set(collapsed, section._k, !collapsed[section._k])">{{ collapsed[section._k] ? '▾' : '▴' }}</button>
            <button v-if="section.is_active" type="button" class="danger" title="حذف یا بایگانی بخش" @click="$emit('remove-section', section)"><VnIcon name="trash" /></button>
            <button v-else type="button" title="بازگرداندن" @click="section.is_active = true"><VnIcon name="undo" /></button>
          </div>
        </header>
        <draggable v-show="!collapsed[section._k]" :list="section.questions" :group="{ name: 'questions', pull: true, put: ['questions'] }" handle=".q-grip"
                   :animation="160" ghost-class="dz-ghost" class="qlist" :class="{ empty: !section.questions.length }" @add="event => onQuestionAdded(section, event)">
          <QuestionCard v-for="(question, qIndex) in section.questions" v-show="showArchived || question.is_active" :key="question._k"
                        :question="question" :index="qIndex" :number="numberOf(section, question)" :last="qIndex === section.questions.length - 1"
                        :selected="isSelected(question)" :types-by-id="typesById" :presets="presets"
                        :photo-count="photoCounts[ref(question)] || 0" :problem="problems[question._k]"
                        @select="select({ kind: 'question', item: question, section })"
                        @move="step => move(section.questions, qIndex, step)"
                        @duplicate="$emit('duplicate-question', { section, index: qIndex })"
                        @remove="$emit('remove-question', { section, question })"
                        @restore="question.is_active = true" />
        </draggable>
        <p v-if="!collapsed[section._k] && !section.questions.length" class="drop-hint">یک نوع پرسش را از ستون اجزا اینجا بکشید.</p>
      </section>
    </draggable>
    <p v-if="!branch.sections.length" class="lane-empty">هنوز بخشی ندارید. «بخش تازه» را از ستون اجزا بکشید یا روی آن کلیک کنید.</p>

    <div class="lane-head photos-head">
      <h2><VnIcon name="camera" />مراحل تصویر</h2>
      <span class="lane-meta">{{ fa(branch.photos.filter(p => p.is_active).length) }} مرحله · در اپ پس از پرسش‌ها می‌آیند</span>
    </div>
    <draggable :list="branch.photos" :group="{ name: 'photos', pull: false, put: ['photos'] }" handle=".p-grip" :animation="160"
               ghost-class="dz-ghost" class="photos" :class="{ empty: !branch.photos.length }" @add="onPhotoAdded">
      <div v-for="(photo, pIndex) in branch.photos" v-show="showArchived || photo.is_active" :key="photo._k" class="pcard"
           :class="{ selected: isSelected(photo), archived: !photo.is_active }" role="button" tabindex="0"
           @click="select({ kind: 'photo', item: photo })" @keydown.enter.self.prevent="select({ kind: 'photo', item: photo })">
        <span class="grip p-grip" aria-hidden="true"><VnIcon name="grip" /></span>
        <span class="picon" :class="{ entry: photo.name === entryPhoto }"><VnIcon :name="photo.name === entryPhoto ? 'door' : 'camera'" /></span>
        <div class="pbody">
          <strong>{{ photo.verbose_name || 'بی‌نام' }}</strong>
          <small>
            <span class="vn-num">{{ fa(photo.min) }} تا {{ fa(photo.max) }}</span> تصویر
            <template v-if="photo.is_mandatory"> · اجباری</template>
            <template v-if="photo.name === entryPhoto"> · تصویر ورود</template>
            <template v-if="linkedText(photo)"> · وصل به «{{ linkedText(photo) }}»</template>
            <template v-if="photo.photos"> · {{ fa(photo.photos) }} عکس ثبت‌شده</template>
          </small>
          <span v-if="problems[photo._k]" class="pproblem" :class="problems[photo._k].level">{{ problems[photo._k].message }}</span>
        </div>
        <div class="ptools" @click.stop>
          <button type="button" title="بالا" :disabled="pIndex === 0" @click="move(branch.photos, pIndex, -1)">▲</button>
          <button type="button" title="پایین" :disabled="pIndex === branch.photos.length - 1" @click="move(branch.photos, pIndex, 1)">▼</button>
          <button v-if="photo.is_active" type="button" class="danger" title="حذف یا بایگانی" @click="$emit('remove-photo', photo)"><VnIcon :name="photo.photos ? 'archive' : 'trash'" /></button>
          <button v-else type="button" title="بازگرداندن" @click="photo.is_active = true"><VnIcon name="undo" /></button>
        </div>
      </div>
    </draggable>
    <p v-if="!branch.photos.length" class="lane-empty">مرحله‌ی تصویری ندارید. «مرحله‌ی تصویر» را از ستون اجزا اینجا بکشید.</p>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import VnIcon from '@/components/VnIcon/index.vue';
import QuestionCard from './QuestionCard.vue';
import { fa, questionRef } from '../designerModel';

export default {
  name: 'Canvas',
  components: { draggable, VnIcon, QuestionCard },
  props: {
    branch: { type: Object, required: true },
    selectedKey: { type: String, default: '' },
    typesById: { type: Object, required: true },
    presets: { type: Object, required: true },
    reportCategories: { type: Array, default: () => [] },
    problems: { type: Object, default: () => ({}) },
    showArchived: { type: Boolean, default: false },
    entryPhoto: { type: String, default: 'StoreFront' },
    questionTitles: { type: Object, default: () => ({}) },
  },
  data: () => ({ collapsed: {} }),
  computed: {
    activeSections() { return this.branch.sections.filter(section => section.is_active); },
    activeQuestions() { return this.activeSections.reduce((sum, section) => sum + section.questions.filter(q => q.is_active).length, 0); },
    photoCounts() {
      return this.branch.photos.reduce((counts, photo) => {
        if (photo.question && photo.is_active) counts[photo.question] = (counts[photo.question] || 0) + 1;
        return counts;
      }, {});
    },
  },
  methods: {
    fa,
    ref: questionRef,
    // numbering follows what is on screen, so hidden archived rows leave no gaps
    numberOf(section, question) { return section.questions.filter(row => this.showArchived || row.is_active).indexOf(question) + 1; },
    isSelected(item) { return !!item && item._k === this.selectedKey; },
    select(target) { this.$emit('select', target); },
    reportName(id) { const row = this.reportCategories.find(item => item.id === id); return row ? row.verbose_name : ''; },
    linkedText(photo) { return photo.question ? (this.questionTitles[photo.question] || 'پرسشی بیرون از این فرم').slice(0, 36) : ''; },
    move(list, index, step) {
      const target = index + step;
      if (target < 0 || target >= list.length) return;
      list.splice(target, 0, list.splice(index, 1)[0]);
    },
    // dropped from the palette: number it and select it so the inspector opens on the new row
    onSectionAdded(event) {
      const section = this.branch.sections[event.newIndex];
      if (!section.verbose_name) section.verbose_name = `بخش ${fa(this.branch.sections.length)}`;
      this.select({ kind: 'section', item: section });
    },
    onQuestionAdded(section, event) {
      const question = section.questions[event.newIndex];
      if (event.pullMode === 'clone' || !question.id) this.select({ kind: 'question', item: question, section });
    },
    onPhotoAdded(event) {
      const photo = this.branch.photos[event.newIndex];
      if (!photo.verbose_name) photo.verbose_name = `مرحله‌ی تصویر ${fa(this.branch.photos.length)}`;
      this.select({ kind: 'photo', item: photo });
    },
  },
};
</script>

<style scoped>
.canvas { display: grid; gap: 12px; align-content: start; }
.lane-head { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.lane-head h2 { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 14px; font-weight: 900; color: var(--navy-900); }
.lane-head h2 .vn-icon { --icon-accent: var(--teal-500); }
.lane-meta { font-size: 11.5px; color: var(--text-3); }
.photos-head { margin-top: 12px; }
.sections, .photos { display: grid; gap: 12px; min-height: 56px; }
.sections.empty, .photos.empty { border: 2px dashed var(--line-strong); border-radius: var(--r-md); background: var(--surface); }
.lane-empty { margin: -60px 0 0; padding: 0 16px 18px; text-align: center; font-size: 12px; color: var(--text-3); pointer-events: none; }
.sblock { border: 1px solid var(--line); border-radius: var(--r-md); background: var(--surface-sunken); overflow: hidden; }
.sblock.selected { border-color: var(--navy-900); box-shadow: 4px 4px 0 var(--navy-900); }
.sblock.archived { opacity: .7; }
.shead { position: relative; display: flex; align-items: center; gap: 10px; padding: 10px 12px 10px 10px; background: var(--surface); border-bottom: 1px solid var(--line); cursor: pointer; }
.shead::before { content: ''; position: absolute; inset-inline-start: 0; top: 10px; bottom: 10px; width: 4px; background: var(--teal-500); transform: skewY(var(--slant)); }
.sblock.archived .shead::before { background: var(--text-4); }
.shead:focus-visible { outline: 3px solid var(--focus); outline-offset: -3px; }
.grip { color: var(--text-4); cursor: grab; font-size: 14px; }
.sindex { width: 24px; height: 24px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--teal-500); color: var(--navy-900); font-size: 12px; font-weight: 900; }
.stitle { display: grid; min-width: 0; flex: 1; }
.stitle strong { font-size: 14px; font-weight: 900; color: var(--text); }
.stitle small { font-size: 11px; color: var(--text-3); }
.stools { display: flex; align-items: center; gap: 4px; }
.stools button { min-width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; gap: 4px; padding: 0 8px; border: 1px solid transparent;
  border-radius: var(--r-xs); background: transparent; color: var(--text-3); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.stools button .vn-icon { width: 15px; height: 15px; }
.stools button:hover { background: var(--navy-50); color: var(--navy-900); }
.stools .add-q { border-color: var(--line-strong); color: var(--navy-900); }
.stools .danger:hover { background: var(--pink-50); color: var(--pink-700); }
.qlist { display: grid; gap: 8px; padding: 10px; min-height: 64px; }
.qlist.empty { margin: 10px; border: 2px dashed var(--line-strong); border-radius: var(--r-sm); background: var(--surface); }
.drop-hint { margin: -66px 0 26px; text-align: center; font-size: 12px; color: var(--text-3); pointer-events: none; }
.pcard { display: grid; grid-template-columns: auto auto 1fr auto; align-items: center; gap: 10px; padding: 10px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--surface); cursor: pointer; }
.pcard.selected { border-color: var(--navy-900); box-shadow: 4px 4px 0 var(--amber-500); }
.pcard.archived { opacity: .6; }
.pcard:focus-visible { outline: 3px solid var(--focus); outline-offset: 1px; }
.picon { width: 34px; height: 34px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--amber-50); color: var(--navy-900); --icon-accent: var(--amber-500); }
.picon.entry { background: var(--navy-900); color: #fff; }
.pbody { display: grid; min-width: 0; }
.pbody strong { font-size: 13px; font-weight: 800; }
.pbody small { font-size: 11px; color: var(--text-3); }
.pproblem { font-size: 11px; font-weight: 700; }
.pproblem.error { color: var(--pink-700); }
.pproblem.warning { color: var(--amber-700); }
.ptools { display: flex; gap: 2px; }
.ptools button { width: 28px; height: 28px; display: grid; place-items: center; border: 0; border-radius: var(--r-xs); background: transparent; color: var(--text-3); font-size: 10px; cursor: pointer; }
.ptools button .vn-icon { width: 15px; height: 15px; }
.ptools button:hover:not(:disabled) { background: var(--navy-50); color: var(--navy-900); }
.ptools .danger:hover { background: var(--pink-50); color: var(--pink-700); }
.ptools button:disabled { opacity: .3; }
</style>
